import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { UIConfigProvider } from "@/context/UIConfigContext";
import { ThemeLoader } from "@/components/ThemeLoader";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import SimulatorPage from "@/pages/SimulatorPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminIRS from "@/pages/admin/AdminIRS";
import AdminIRSYouth from "@/pages/admin/AdminIRSYouth";
import AdminSS from "@/pages/admin/AdminSS";
import AdminCosts from "@/pages/admin/AdminCosts";
import AdminCalendar from "@/pages/admin/AdminCalendar";
import AdminMargins from "@/pages/admin/AdminMargins";
import AdminEquipment from "@/pages/admin/AdminEquipment";
import AdminLicenses from "@/pages/admin/AdminLicenses";
import AdminClientSpecialFees from "@/pages/admin/AdminClientSpecialFees";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminApprovers from "@/pages/admin/AdminApprovers";
import AdminBenefits from "@/pages/admin/AdminBenefits";
import AdminCustomBenefits from "@/pages/admin/AdminCustomBenefits";
import AdminPremiums from "@/pages/admin/AdminPremiums";
import AdminFixedBonuses from "@/pages/admin/AdminFixedBonuses";
import AdminFixedPermFees from "@/pages/admin/AdminFixedPermFees";
import AdminMarginColors from "@/pages/admin/AdminMarginColors";
import AdminInternationalCosts from "@/pages/admin/AdminInternationalCosts";
import AdminBusinessSettings from "@/pages/admin/AdminBusinessSettings";
import AdminLayoutPresets from "@/pages/admin/AdminLayoutPresets";
import AdminPermFeeTiers from "@/pages/admin/AdminPermFeeTiers";
import AdminProposalSettings from "@/pages/admin/AdminProposalSettings";
import AdminJobFunctions from "@/pages/admin/AdminJobFunctions";
import AdminTemplates from "@/pages/admin/AdminTemplates";
import AdminTaxRates from "@/pages/admin/AdminTaxRates";
import AdminClients from "@/pages/admin/AdminClients";
import AdminClientDetail from "@/pages/admin/AdminClientDetail";
import AdminEmailTemplates from "@/pages/admin/AdminEmailTemplates";
import CandidateLinks from "@/pages/CandidateLinks";
import SharedSimulator from "@/pages/SharedSimulator";
import ProposalPage from "@/pages/ProposalPage";
import ProposalSignPage from "@/pages/ProposalSignPage";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">A carregar...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/simulator" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <UIConfigProvider>
        <ThemeLoader />
        <div className="App min-h-screen bg-background" style={{ fontFamily: 'var(--ui-font-family)' }}>
          <BrowserRouter>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
            
            {/* Protected User Routes */}
            <Route
              path="/simulator"
              element={
                <ProtectedRoute>
                  <SimulatorPage />
                </ProtectedRoute>
              }
            />
            
            {/* Protected Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/layout-presets"
              element={
                <ProtectedRoute adminOnly>
                  <AdminLayoutPresets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/irs"
              element={
                <ProtectedRoute adminOnly>
                  <AdminIRS />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/irs-jovem"
              element={
                <ProtectedRoute adminOnly>
                  <AdminIRSYouth />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/ss"
              element={
                <ProtectedRoute adminOnly>
                  <AdminSS />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/costs"
              element={
                <ProtectedRoute adminOnly>
                  <AdminCosts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/calendar"
              element={
                <ProtectedRoute adminOnly>
                  <AdminCalendar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/margins"
              element={
                <ProtectedRoute adminOnly>
                  <AdminMargins />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/equipment"
              element={
                <ProtectedRoute adminOnly>
                  <AdminEquipment />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/licenses"
              element={
                <ProtectedRoute adminOnly>
                  <AdminLicenses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/client-special-fees"
              element={
                <ProtectedRoute adminOnly>
                  <AdminClientSpecialFees />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute adminOnly>
                  <AdminUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/approvers"
              element={
                <ProtectedRoute adminOnly>
                  <AdminApprovers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/benefits"
              element={
                <ProtectedRoute adminOnly>
                  <AdminBenefits />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/custom-benefits"
              element={
                <ProtectedRoute adminOnly>
                  <AdminCustomBenefits />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/premiums"
              element={
                <ProtectedRoute adminOnly>
                  <AdminPremiums />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/fixed-bonuses"
              element={
                <ProtectedRoute adminOnly>
                  <AdminFixedBonuses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/fixed-perm-fees"
              element={
                <ProtectedRoute adminOnly>
                  <AdminFixedPermFees />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/margin-colors"
              element={
                <ProtectedRoute adminOnly>
                  <AdminMarginColors />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/international-costs"
              element={
                <ProtectedRoute adminOnly>
                  <AdminInternationalCosts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/business-settings"
              element={
                <ProtectedRoute adminOnly>
                  <AdminBusinessSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/perm-fee-tiers"
              element={
                <ProtectedRoute adminOnly>
                  <AdminPermFeeTiers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/proposal-settings"
              element={
                <ProtectedRoute adminOnly>
                  <AdminProposalSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/job-functions"
              element={
                <ProtectedRoute adminOnly>
                  <AdminJobFunctions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/templates"
              element={
                <ProtectedRoute adminOnly>
                  <AdminTemplates />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/tax-rates"
              element={
                <ProtectedRoute adminOnly>
                  <AdminTaxRates />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/clients"
              element={
                <ProtectedRoute adminOnly>
                  <AdminClients />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/clients/:id"
              element={
                <ProtectedRoute adminOnly>
                  <AdminClientDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/email-templates"
              element={
                <ProtectedRoute adminOnly>
                  <AdminEmailTemplates />
                </ProtectedRoute>
              }
            />
            <Route
              path="/candidates"
              element={
                <ProtectedRoute>
                  <CandidateLinks />
                </ProtectedRoute>
              }
            />
            
            {/* Shared Simulator (public access) */}
            <Route path="/simulator/shared/:id" element={<SharedSimulator />} />
            
            {/* Proposal Page (public access for sharing and PDF) */}
            <Route path="/proposal/:id" element={<ProposalPage />} />
            
            {/* Proposal Sign Page (public - candidate signs) */}
            <Route path="/proposal/sign/:token" element={<ProposalSignPage />} />
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
      </UIConfigProvider>
    </AuthProvider>
  );
}

export default App;
